var _path, _path2;
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import * as React from 'react';
import { colors } from '@ovpn-ui/styles';
import { useCombinedRefs } from '@ovpn-ui/utils';
import Text from '../Text';
import getBoxStyles from '../_internal/utils/getBoxStyles';
import HiddenInput from '../_internal/components/HiddenInput';
import useCheckedProp from '../_internal/hooks/useCheckedProp';
import maybeElementId from '../_internal/utils/maybeElementId';
import maybeDataTestId from '../_internal/utils/maybeDataTestId';
import { Container, Appearance, ContentWrapper } from './Checkbox.styles';
import { getIndeterminateBarWidth, getTickDimension } from './Checkbox.utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function Checkbox(props, ref) {
  const {
    // state
    disabled = undefined,
    indeterminate,
    // appearance
    className = '',
    direction = 'ltr',
    // text contents
    title,
    description,
    // appearance variant
    rounded = false,
    // size variant
    size = 'md',
    // accessibility props
    'aria-label': ariaLabel,
    'data-testid': dataTestId,
    id,
    ..._htmlAttributes
  } = props;
  const innerRef = React.useRef(null);
  const combinedInputRef = useCombinedRefs(ref, innerRef);
  const {
    checked,
    onChange
  } = useCheckedProp(props);
  const {
    boxStyles: boxProps,
    restProps: htmlAttributes
  } = getBoxStyles(_htmlAttributes);
  const shouldRenderSmallRadio = size === 'sm';
  const tickDimension = React.useMemo(() => getTickDimension(rounded, size), [rounded, size]);
  const barWidth = React.useMemo(() => getIndeterminateBarWidth(rounded, size), [rounded, size]);
  return /*#__PURE__*/_jsxs(Container, {
    forwardedAs: "label",
    className: clsx('ovpn-checkbox-container', className),
    $disabled: disabled,
    ...boxProps,
    tabIndex: disabled ? -1 : undefined,
    "aria-label": ariaLabel || title,
    $direction: direction,
    ...maybeDataTestId(dataTestId),
    onClick: e => {
      if (e.target !== innerRef.current) {
        e.stopPropagation();
      }
    },
    children: [/*#__PURE__*/_jsx(HiddenInput, {
      ...htmlAttributes,
      className: clsx('ovpn-checkbox-input', {
        'is-disabled': disabled,
        'is-indeterminate': indeterminate,
        'is-checked': checked
      }),
      type: "checkbox",
      "aria-checked": checked,
      "aria-disabled": disabled ? true : undefined,
      disabled: disabled,
      ref: combinedInputRef,
      onChange: onChange,
      checked: checked,
      ...maybeElementId(id)
    }), /*#__PURE__*/_jsxs(Appearance, {
      className: "ovpn-checkbox-appearance",
      disabled: disabled,
      checked: checked,
      indeterminate: indeterminate,
      small: shouldRenderSmallRadio,
      rounded: rounded,
      withContent: isNil(title) ? undefined : true,
      children: [!indeterminate && checked && /*#__PURE__*/_jsx("svg", {
        width: tickDimension.width,
        height: tickDimension.height,
        viewBox: "0 0 15 10",
        fill: "none",
        xmlns: "http://www.w3.org/2000/svg",
        ...maybeDataTestId(dataTestId, 'checkbox-checked-svg'),
        children: _path || (_path = /*#__PURE__*/_jsx("path", {
          d: "M1.16675 4.99998L5.33341 9.16665L13.6667 0.833313",
          stroke: "currentColor",
          strokeWidth: "1.33333",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }))
      }), indeterminate && /*#__PURE__*/_jsx("svg", {
        width: barWidth,
        height: "2",
        viewBox: "0 0 14 2",
        fill: "none",
        xmlns: "http://www.w3.org/2000/svg",
        ...maybeDataTestId(dataTestId, 'checkbox-indeterminate-svg'),
        children: _path2 || (_path2 = /*#__PURE__*/_jsx("path", {
          d: "M1.16675 1H12.8334",
          stroke: "currentColor",
          strokeWidth: "1.33333",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }))
      })]
    }), title && /*#__PURE__*/_jsxs(ContentWrapper, {
      children: [/*#__PURE__*/_jsx(Text, {
        color: colors.neutralTextTitle,
        variant: shouldRenderSmallRadio ? 'textSm' : 'textMd',
        ...maybeElementId(id, 'label'),
        children: title
      }), description && /*#__PURE__*/_jsx(Text, {
        color: colors.neutralTextSubtitle,
        variant: "textSm",
        ...maybeElementId(id, 'description'),
        children: description
      })]
    })]
  });
}
export default /*#__PURE__*/React.forwardRef(Checkbox);